import type { HttpError } from "@microbeta/common-frontend";
import { type UseQueryOptions, useQuery } from "@tanstack/react-query";
import {
  type AreaStatistics,
  type BulkAreaStatistics,
  type GlobalStatistics,
  getAreaStatistics,
  getBulkAreaStatistics,
  getGlobalStatistics,
} from "./statistics";

export const statisticsKeys = {
  all: ["statistics"] as const,
  global: () => [...statisticsKeys.all, "global"] as const,
  areas: () => [...statisticsKeys.all, "areas"] as const,
  area: (id: string) => [...statisticsKeys.areas(), id] as const,
  areasBulk: (ids: string[]) =>
    [...statisticsKeys.areas(), "bulk", ids.sort()] as const,
};

/**
 * Hook to fetch global platform statistics
 */
export const useGetGlobalStatistics = (
  options?: Omit<
    UseQueryOptions<GlobalStatistics, HttpError>,
    "queryKey" | "queryFn"
  >
) => {
  return useQuery<GlobalStatistics, HttpError>({
    queryKey: statisticsKeys.global(),
    queryFn: getGlobalStatistics,
    staleTime: 5 * 60 * 1000, // 5 minutes - global stats don't change frequently
    ...options,
  });
};

/**
 * Hook to fetch statistics for a specific area
 */
export const useGetAreaStatistics = (
  areaId: string | undefined,
  options?: Omit<
    UseQueryOptions<AreaStatistics, HttpError>,
    "queryKey" | "queryFn" | "enabled"
  >
) => {
  return useQuery<AreaStatistics, HttpError>({
    queryKey: areaId
      ? statisticsKeys.area(areaId)
      : ["statistics", "areas", "undefined"],
    queryFn: () =>
      areaId ? getAreaStatistics(areaId) : Promise.reject("No area ID"),
    enabled: !!areaId,
    staleTime: 2 * 60 * 1000, // 2 minutes - individual stats can be cached briefly
    ...options,
  });
};

/**
 * Hook to fetch statistics for multiple areas at once (bulk operation)
 * This is ideal for the homepage where you need stats for multiple areas
 */
export const useGetBulkAreaStatistics = (
  areaIds: string[],
  options?: Omit<
    UseQueryOptions<BulkAreaStatistics, HttpError>,
    "queryKey" | "queryFn" | "enabled"
  >
) => {
  const validAreaIds = areaIds.filter(Boolean);

  return useQuery<BulkAreaStatistics, HttpError>({
    queryKey: statisticsKeys.areasBulk(validAreaIds),
    queryFn: () => getBulkAreaStatistics(validAreaIds),
    enabled: validAreaIds.length > 0,
    staleTime: 2 * 60 * 1000, // 2 minutes
    ...options,
  });
};
